import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Sun, 
  Zap, 
  DollarSign, 
  Home, 
  CheckCircle2,
  ArrowRight,
  Phone,
  PhoneCall,
  Star,
  Clock,
  XCircle,
  Upload,
  Users,
  MapPin,
  Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Service areas by territory - Richmond Office (Hampton to borderline Richmond)
const richmondAreas = [
  { value: "Richmond", label: "Richmond" },
  { value: "Henrico", label: "Henrico County" },
  { value: "Chesterfield", label: "Chesterfield County" },
  { value: "Hanover", label: "Hanover County" },
  { value: "Mechanicsville", label: "Mechanicsville" },
  { value: "Glen Allen", label: "Glen Allen" },
  { value: "Midlothian", label: "Midlothian" },
  { value: "Short Pump", label: "Short Pump" },
  { value: "Ashland", label: "Ashland" },
  { value: "Colonial Heights", label: "Colonial Heights" },
  { value: "Hopewell", label: "Hopewell" },
  { value: "Petersburg", label: "Petersburg" },
  { value: "Prince George", label: "Prince George County" },
  { value: "Dinwiddie", label: "Dinwiddie County" },
  { value: "Charles City", label: "Charles City County" },
  { value: "New Kent", label: "New Kent County" },
];

// Williamsburg Office (Between Hampton and Richmond, Gwynn Island to borderline Suffolk)
const williamsburgAreas = [
  { value: "Williamsburg", label: "Williamsburg" },
  { value: "James City County", label: "James City County" },
  { value: "York County", label: "York County" },
  { value: "Yorktown", label: "Yorktown" },
  { value: "Newport News", label: "Newport News" },
  { value: "Hampton", label: "Hampton" },
  { value: "Poquoson", label: "Poquoson" },
  { value: "Gloucester", label: "Gloucester County" },
  { value: "Mathews", label: "Mathews County" },
  { value: "Gwynn Island", label: "Gwynn Island" },
  { value: "Middlesex", label: "Middlesex County" },
  { value: "King William", label: "King William County" },
  { value: "King and Queen", label: "King and Queen County" },
  { value: "Essex", label: "Essex County" },
  { value: "Lancaster", label: "Lancaster County" },
  { value: "Northumberland", label: "Northumberland County" },
  { value: "Richmond County", label: "Richmond County" },
  { value: "Westmoreland", label: "Westmoreland County" },
  { value: "Surry", label: "Surry County" },
  { value: "Isle of Wight", label: "Isle of Wight County" },
  { value: "Smithfield", label: "Smithfield" },
  { value: "Carrollton", label: "Carrollton" },
  { value: "Toano", label: "Toano" },
  { value: "Norge", label: "Norge" },
  { value: "Lightfoot", label: "Lightfoot" },
  { value: "Grafton", label: "Grafton" },
  { value: "Seaford", label: "Seaford" },
  { value: "Denbigh", label: "Denbigh" },
  { value: "Phoebus", label: "Phoebus" },
  { value: "Buckroe Beach", label: "Buckroe Beach" },
  { value: "Fox Hill", label: "Fox Hill" },
];

// Co-op power companies in the service area
const coopPowerCompanies = [
  { value: "rappahannock_electric", label: "Rappahannock Electric Cooperative" },
  { value: "northern_neck", label: "Northern Neck Electric Cooperative" },
  { value: "prince_george", label: "Prince George Electric Cooperative" },
  { value: "southside", label: "Southside Electric Cooperative" },
  { value: "mecklenburg", label: "Mecklenburg Electric Cooperative" },
  { value: "other_coop", label: "Other Co-op" },
];

const benefits = [
  { icon: DollarSign, title: "Zero Down Payment", desc: "No upfront costs with PPA" },
  { icon: Zap, title: "Lower Bills", desc: "Save up to 30% on electricity" },
  { icon: Home, title: "Increase Home Value", desc: "Solar adds property value" },
  { icon: Sun, title: "Clean Energy", desc: "Reduce your carbon footprint" }
];

const defaultTestimonials = [
  {
    content: "Anthony made the whole process so easy. Professional, friendly, and really knew their stuff about solar. Highly recommend!",
    author_name: "Betsy S.",
    location: "Norfolk, VA"
  },
  {
    content: "Ion Solar saved us over $150/month on our electricity bills! The PPA made going solar affordable with no upfront cost.",
    author_name: "The Johnson Family",
    location: "Richmond, VA"
  },
  {
    content: "Best decision we ever made for our home. Zero down, lower bills, and we're helping the environment.",
    author_name: "Mike T.",
    location: "Virginia Beach"
  }
];

// Testimonial Form Component
function TestimonialForm() {
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [testimonialData, setTestimonialData] = useState({
    author_name: "",
    location: "",
    content: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/testimonials`, testimonialData);
      setSubmitted(true);
      toast.success("Thank you for your testimonial!");
    } catch (error) {
      toast.success("Thank you for your testimonial!");
      setSubmitted(true);
    }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="bg-[#1A4D2E]/5 rounded-xl p-4 text-center">
        <CheckCircle2 className="w-8 h-8 text-[#1A4D2E] mx-auto mb-2" />
        <p className="text-sm text-[#1A4D2E] font-medium">Thank you for sharing your experience!</p>
      </div>
    );
  }

  if (!showForm) {
    return (
      <button
        onClick={() => setShowForm(true)}
        className="w-full bg-[#1A4D2E]/10 hover:bg-[#1A4D2E]/20 text-[#1A4D2E] rounded-xl p-4 transition-colors"
      >
        <Star className="w-5 h-5 mx-auto mb-2" />
        <p className="text-sm font-medium">Share Your Experience</p>
        <p className="text-xs text-slate-500 mt-1">Help others learn about Ion Solar</p>
      </button>
    );
  }

  return (
    <div className="bg-white rounded-xl p-4 border border-slate-200">
      <h3 className="font-semibold text-slate-900 mb-3 text-sm">Share Your Experience</h3>
      <form onSubmit={handleSubmit} className="space-y-3">
        <Input
          placeholder="Your Name"
          value={testimonialData.author_name}
          onChange={(e) => setTestimonialData({...testimonialData, author_name: e.target.value})}
          required
          className="h-10 text-sm"
        />
        <Input
          placeholder="City, VA"
          value={testimonialData.location}
          onChange={(e) => setTestimonialData({...testimonialData, location: e.target.value})}
          required
          className="h-10 text-sm"
        />
        <textarea
          placeholder="Tell us about your experience..."
          value={testimonialData.content}
          onChange={(e) => setTestimonialData({...testimonialData, content: e.target.value})}
          required
          rows={3}
          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/20"
        />
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => setShowForm(false)}
            className="flex-1 h-10 text-sm"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={submitting || !testimonialData.content}
            className="flex-1 h-10 bg-[#1A4D2E] hover:bg-[#153d25] text-sm"
          >
            {submitting ? "Submitting..." : "Submit"}
          </Button>
        </div>
      </form>
    </div>
  );
}

export default function LeadCapture() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [disqualified, setDisqualified] = useState(false);
  const [disqualificationReason, setDisqualificationReason] = useState("");
  const [roofQuoteNeeded, setRoofQuoteNeeded] = useState(false);
  const [roofDisqualified, setRoofDisqualified] = useState(false);
  const [billFile, setBillFile] = useState(null);
  const [pendingConfirmation, setPendingConfirmation] = useState(false);
  const [recentLeads, setRecentLeads] = useState([]);
  const [testimonials, setTestimonials] = useState(defaultTestimonials);
  
  // Test mode state
  const [isTestMode, setIsTestMode] = useState(false);
  
  // Step 5 & 6 - Booking state
  const [leadId, setLeadId] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedBookingDay, setSelectedBookingDay] = useState(null);
  const [selectedBookingSlot, setSelectedBookingSlot] = useState(null);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [slotsLoading, setSlotsLoading] = useState(false);
  
  // Call booking (Step 5)
  const [callBooking, setCallBooking] = useState(null); // Stored call booking after confirmation
  
  // Appointment booking (Step 6)
  const [appointmentBooking, setAppointmentBooking] = useState(null); // Stored appointment booking after confirmation
  const [appointmentSlots, setAppointmentSlots] = useState([]); // Slots for Step 6 (excludes call slot)
  const [selectedAppointmentDay, setSelectedAppointmentDay] = useState(null);
  const [selectedAppointmentSlot, setSelectedAppointmentSlot] = useState(null);
  
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "VA",
    zip_code: "",
    service_area: "",
    power_company: "",
    roof_type: "",
    bill_filename: "",
    is_homeowner: null,
    credit_score_range: "",
    makes_on_time_payments: null,
    has_tree_shading: null,
    open_to_tree_removal: null
  });

  // Check for test mode on load (URL param or session storage)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('testmode') === '1') {
      setIsTestMode(true);
    }
    
    // Check for existing booking in session storage (success screen persistence)
    const savedBooking = sessionStorage.getItem('ionSolarBooking');
    if (savedBooking) {
      try {
        const bookingData = JSON.parse(savedBooking);
        if (bookingData.callBooking && bookingData.appointmentBooking) {
          setCallBooking(bookingData.callBooking);
          setAppointmentBooking(bookingData.appointmentBooking);
          setLeadId(bookingData.leadId);
          setFormData(prev => ({
            ...prev,
            first_name: bookingData.firstName || '',
            last_name: bookingData.lastName || ''
          }));
          setSubmitted(true);
        }
      } catch (e) {
        console.log("Could not restore booking from session");
        sessionStorage.removeItem('ionSolarBooking');
      }
    }
  }, []);

  // Auto-enable test mode when name contains TEST
  useEffect(() => {
    const fullName = `${formData.first_name} ${formData.last_name}`.toLowerCase();
    if (fullName.includes('test') && !isTestMode) {
      setIsTestMode(true);
    }
  }, [formData.first_name, formData.last_name, isTestMode]);

  // Fetch recent leads for social proof
  useEffect(() => {
    const fetchRecentLeads = async () => {
      try {
        const response = await axios.get(`${API}/leads/public`);
        setRecentLeads(response.data);
      } catch (error) {
        console.log("Could not fetch recent leads");
      }
    };
    fetchRecentLeads();
  }, []);

  // Fetch approved testimonials
  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await axios.get(`${API}/testimonials`);
        if (response.data && response.data.length > 0) {
          setTestimonials([...response.data, ...defaultTestimonials].slice(0, 6));
        }
      } catch (error) {
        console.log("Using default testimonials");
      }
    };
    fetchTestimonials();
  }, []);

  // Fix 1: Scroll to top on every step change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  // Also scroll to top when showing disqualified or success screens
  useEffect(() => {
    if (disqualified || submitted) {
      window.scrollTo(0, 0);
    }
  }, [disqualified, submitted]);

  const handleDisqualification = (reason) => {
    setDisqualificationReason(reason);
    setDisqualified(true);
  };

  // Fix 3: Reset form and go back to Step 1
  const handleStartOver = () => {
    // Clear session storage
    sessionStorage.removeItem('ionSolarBooking');
    
    // Reset all form state
    setStep(1);
    setLoading(false);
    setSubmitted(false);
    setDisqualified(false);
    setDisqualificationReason("");
    setRoofQuoteNeeded(false);
    setRoofDisqualified(false);
    setBillFile(null);
    setPendingConfirmation(false);
    setLeadId(null);
    setAvailableSlots([]);
    setSelectedBookingDay(null);
    setSelectedBookingSlot(null);
    setBookingLoading(false);
    setSlotsLoading(false);
    setCallBooking(null);
    setAppointmentBooking(null);
    setAppointmentSlots([]);
    setSelectedAppointmentDay(null);
    setSelectedAppointmentSlot(null);
    setIsTestMode(false);
    setFormData({
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      address: "",
      city: "",
      state: "VA",
      zip_code: "",
      service_area: "",
      power_company: "",
      roof_type: "",
      bill_filename: "",
      is_homeowner: null,
      credit_score_range: "",
      makes_on_time_payments: null,
      has_tree_shading: null,
      open_to_tree_removal: null
    });
    window.scrollTo(0, 0);
  };

  const handleServiceAreaChange = (value) => {
    updateForm("service_area", value);
  };

  const handlePowerCompanyChange = (value) => {
    updateForm("power_company", value);
    // Any non-Dominion power company requires confirmation
    if (value !== "dominion" && value !== "") {
      setPendingConfirmation(true);
    } else {
      setPendingConfirmation(false);
    }
  };

  const updateForm = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Fetch available booking slots
  const fetchBookingSlots = async () => {
    setSlotsLoading(true);
    try {
      const response = await axios.get(`${API}/booking/slots`);
      const days = response.data.days || [];
      setAvailableSlots(days);
      
      // Auto-select the first day that has at least one available slot
      const firstAvailableDay = days.find(day => 
        day.slots && day.slots.some(slot => slot.is_available)
      );
      
      if (firstAvailableDay) {
        setSelectedBookingDay(firstAvailableDay);
      } else if (days.length > 0) {
        // Fallback to first day if none have availability
        setSelectedBookingDay(days[0]);
      }
    } catch (error) {
      console.error("Error fetching booking slots:", error);
      toast.error("Could not load available times. Please refresh the page.");
    } finally {
      setSlotsLoading(false);
    }
  };

  const handleSubmit = async () => {
    // Prevent duplicate lead creation if already submitted
    if (leadId) {
      // Already have a lead, just go to booking step
      fetchBookingSlots();
      setStep(4);
      return;
    }
    
    setLoading(true);
    try {
      // Create FormData for file upload
      const submitData = new FormData();
      submitData.append("first_name", formData.first_name);
      submitData.append("last_name", formData.last_name);
      submitData.append("email", formData.email);
      submitData.append("phone", formData.phone);
      submitData.append("address", formData.address);
      submitData.append("city", formData.city);
      submitData.append("state", formData.state);
      submitData.append("zip_code", formData.zip_code);
      submitData.append("roof_type", formData.roof_type);
      submitData.append("is_homeowner", formData.is_homeowner);
      submitData.append("credit_score_range", formData.credit_score_range);
      submitData.append("makes_on_time_payments", formData.makes_on_time_payments || false);
      submitData.append("preferred_date", "");
      submitData.append("preferred_time", "");
      submitData.append("service_area", formData.service_area);
      submitData.append("power_company", formData.power_company);
      submitData.append("pending_confirmation", pendingConfirmation);
      submitData.append("has_tree_shading", formData.has_tree_shading || false);
      submitData.append("open_to_tree_removal", formData.open_to_tree_removal || false);
      submitData.append("is_test", isTestMode);
      
      if (billFile) {
        submitData.append("bill_file", billFile);
      }
      
      const response = await axios.post(`${API}/leads/upload`, submitData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      // Store the lead ID for booking
      setLeadId(response.data.id);
      
      // Move to Step 4 - Schedule Call
      fetchBookingSlots();
      setStep(4);
      toast.success("Great! Now let's schedule a quick call.");
      
    } catch (error) {
      console.error("Error submitting lead:", error);
      const errorDetail = error.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(errorDetail);
    } finally {
      setLoading(false);
    }
  };

  // Step 5: Confirm CALL booking
  const handleCallConfirm = async () => {
    if (!selectedBookingSlot) {
      toast.error("Please select a time slot for your call");
      return;
    }
    
    setBookingLoading(true);
    try {
      const response = await axios.post(`${API}/booking/confirm`, {
        lead_id: leadId,
        slot_datetime_utc: selectedBookingSlot.datetime_utc,
        is_request_only: selectedBookingSlot.is_request_only || false,
        booking_type: "call",
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zip_code: formData.zip_code,
        is_test: isTestMode
      });
      
      // Store call booking and move to Step 5
      setCallBooking(response.data);
      toast.success("Call scheduled! Now pick a time for your in-home consultation.");
      
      // Fetch fresh slots for appointment (will exclude call slot via 2.5hr buffer)
      await fetchAppointmentSlots();
      setStep(5);
      
    } catch (error) {
      console.error("Error confirming call:", error);
      if (error.response?.status === 409) {
        toast.error("This time slot is no longer available. Please select another time.");
        fetchBookingSlots(); // Refresh slots
      } else {
        const errorMsg = error.response?.data?.detail || "Could not schedule call. Please try again.";
        toast.error(errorMsg);
      }
    } finally {
      setBookingLoading(false);
    }
  };

  // Fetch slots for Step 6 (appointment) - after call is booked
  const fetchAppointmentSlots = async () => {
    setSlotsLoading(true);
    try {
      const response = await axios.get(`${API}/booking/slots`);
      const days = response.data.days || [];
      setAppointmentSlots(days);
      
      // Auto-select the first day that has at least one available slot
      const firstAvailableDay = days.find(day => 
        day.slots && day.slots.some(slot => slot.is_available)
      );
      
      if (firstAvailableDay) {
        setSelectedAppointmentDay(firstAvailableDay);
      } else if (days.length > 0) {
        setSelectedAppointmentDay(days[0]);
      }
    } catch (error) {
      console.error("Error fetching appointment slots:", error);
      toast.error("Could not load available times. Please refresh the page.");
    } finally {
      setSlotsLoading(false);
    }
  };

  // Step 6: Confirm APPOINTMENT booking
  const handleAppointmentConfirm = async () => {
    if (!selectedAppointmentSlot) {
      toast.error("Please select a time slot for your consultation");
      return;
    }
    
    setBookingLoading(true);
    try {
      const response = await axios.post(`${API}/booking/confirm`, {
        lead_id: leadId,
        slot_datetime_utc: selectedAppointmentSlot.datetime_utc,
        is_request_only: selectedAppointmentSlot.is_request_only || false,
        booking_type: "appointment",
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zip_code: formData.zip_code,
        is_test: isTestMode
      });
      
      // Store appointment booking and show success screen
      setAppointmentBooking(response.data);
      setSubmitted(true);
      
      // Save to sessionStorage for persistence on page refresh
      sessionStorage.setItem('ionSolarBooking', JSON.stringify({
        callBooking,
        appointmentBooking: response.data,
        leadId,
        firstName: formData.first_name,
        lastName: formData.last_name
      }));
      
      toast.success("Appointment requested! Anthony will confirm shortly.");
      
    } catch (error) {
      console.error("Error confirming appointment:", error);
      if (error.response?.status === 409) {
        toast.error("This time slot is no longer available. Please select another time.");
        fetchAppointmentSlots(); // Refresh slots
      } else {
        const errorMsg = error.response?.data?.detail || "Could not request appointment. Please try again.";
        toast.error(errorMsg);
      }
    } finally {
      setBookingLoading(false);
    }
  };

  const isStep1Valid = formData.first_name && formData.last_name && formData.email && formData.phone;
  const isTreeShadingValid = formData.has_tree_shading === false || (formData.has_tree_shading === true && formData.open_to_tree_removal === true);
  const isStep2Valid = formData.address && formData.city && formData.zip_code && formData.roof_type && formData.service_area && formData.power_company && !roofDisqualified && billFile && formData.has_tree_shading !== null && isTreeShadingValid;
  const isStep3Valid = formData.is_homeowner === true && formData.credit_score_range && formData.credit_score_range !== "below_630";

  // Show disqualification screen
  if (disqualified) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4" data-testid="lead-capture-disqualified">
        <div className="max-w-md w-full text-center animate-fade-in">
          <div className="w-20 h-20 rounded-full bg-[#A94442]/10 flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-10 h-10 text-[#A94442]" />
          </div>
          <h1 className="text-2xl md:text-3xl font-['Outfit'] font-bold text-slate-900 mb-4">
            We're Sorry
          </h1>
          <p className="text-sm md:text-base text-slate-600 mb-6">
            Unfortunately, you do not meet the requirements for our solar PPA program at this time.
          </p>
          <div className="bg-white rounded-xl p-4 md:p-6 border border-slate-200 mb-6">
            <p className="text-sm text-slate-500 mb-2">Reason:</p>
            <p className="font-medium text-[#A94442]">{disqualificationReason}</p>
          </div>
          <div className="bg-[#1A4D2E]/5 rounded-xl p-4 mb-6">
            <p className="text-sm text-slate-700">
              If your situation changes in the future, please feel free to apply again. 
              We're here to help Virginia homeowners go solar!
            </p>
          </div>
          {/* Fix 3: Submit Another Request button */}
          <Button
            onClick={handleStartOver}
            className="w-full h-12 bg-[#1A4D2E] hover:bg-[#153d24] rounded-full text-base"
            data-testid="submit-another-btn"
          >
            Submit Another Request
          </Button>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4" data-testid="lead-capture-success">
        <div className="max-w-md w-full text-center animate-fade-in">
          {/* Clean Success Screen - No Pending Status */}
          <div className="text-6xl mb-6">🎉</div>
          <h1 className="text-2xl md:text-3xl font-['Outfit'] font-bold text-slate-900 mb-4">
            You're All Set!
          </h1>
          
          <p className="text-base md:text-lg text-slate-600 mb-8 leading-relaxed max-w-sm mx-auto">
            Your call and consultation have been requested. Anthony will confirm 
            your appointment time via email and text — we'll make sure it works 
            perfectly for you.
          </p>
          
          {/* Email Check Notice */}
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 max-w-sm mx-auto mb-8">
            <p className="text-sm text-green-800 font-medium">
              📧 Check your email for confirmation details
            </p>
          </div>
          
          {/* Anthony Card */}
          <div className="bg-white rounded-xl p-4 md:p-6 border border-slate-200 mb-6 max-w-sm mx-auto">
            <div className="flex items-center justify-center gap-4">
              <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-[#A94442] flex items-center justify-center">
                <span className="text-white font-bold text-lg md:text-xl">AF</span>
              </div>
              <div className="text-left">
                <p className="font-['Outfit'] font-semibold text-slate-900">Anthony F.</p>
                <p className="text-sm text-slate-500">Your Solar Consultant</p>
              </div>
            </div>
          </div>

          {/* Back to Home button */}
          <button
            onClick={() => {
              sessionStorage.removeItem('ionSolarBooking');
              handleStartOver();
            }}
            className="px-8 py-3 bg-slate-900 text-white font-semibold rounded-xl hover:bg-slate-800 transition-colors"
            data-testid="back-to-home-btn"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="lead-capture-page">
      {/* Hero Section */}
      <div className="lead-form-hero text-white py-10 md:py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-8 md:w-10 h-8 md:h-10 rounded-xl bg-white/20 flex items-center justify-center">
              <Sun className="w-5 md:w-6 h-5 md:h-6" />
            </div>
            <span className="font-['Outfit'] font-bold text-lg md:text-xl">Ion Solar</span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-['Outfit'] font-bold mb-3 md:mb-4">
            Go Solar with Zero Down
          </h1>
          <p className="text-base md:text-xl text-white/90 max-w-2xl mx-auto px-4">
            Virginia homeowners are saving big with our Power Purchase Agreement. 
            Pick a day and time, and we'll see if you qualify!
          </p>
        </div>
      </div>

      {/* Benefits */}
      <div className="max-w-4xl mx-auto -mt-6 md:-mt-8 px-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
          {benefits.map((benefit, index) => (
            <div 
              key={index}
              className="bg-white rounded-xl p-3 md:p-4 shadow-lg border border-slate-100 text-center stagger-item"
            >
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-[#1A4D2E]/10 flex items-center justify-center mx-auto mb-2 md:mb-3">
                <benefit.icon className="w-5 h-5 md:w-6 md:h-6 text-[#1A4D2E]" />
              </div>
              <h3 className="font-['Outfit'] font-semibold text-slate-900 text-xs md:text-sm">{benefit.title}</h3>
              <p className="text-[10px] md:text-xs text-slate-500 mt-1 hidden md:block">{benefit.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto py-8 md:py-12 px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Form */}
          <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-5 md:p-8" data-testid="lead-form">
            {/* Progress */}
            <div className="flex items-center gap-1 md:gap-2 mb-6 md:mb-8">
              {[1, 2, 3, 4, 5].map((s) => (
                <React.Fragment key={s}>
                  <div 
                    className={`w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center font-semibold text-xs md:text-sm transition-all ${
                      step >= s ? "bg-[#A94442] text-white" : "bg-slate-100 text-slate-400"
                    }`}
                  >
                    {s}
                  </div>
                  {s < 5 && (
                    <div className={`flex-1 h-1 rounded ${step > s ? "bg-[#A94442]" : "bg-slate-100"}`} />
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Step 1: Contact Info */}
            {step === 1 && (
              <div className="space-y-4 md:space-y-6 animate-fade-in">
                {/* Test Mode Banner */}
                {isTestMode && (
                  <div className="bg-yellow-100 border border-yellow-400 rounded-xl p-3 flex items-center gap-2" data-testid="test-mode-banner">
                    <span className="text-lg">🧪</span>
                    <p className="text-sm text-yellow-800 font-medium">
                      TEST MODE — No calendar events will be created
                    </p>
                  </div>
                )}
                
                <div>
                  <h2 className="text-xl md:text-2xl font-['Outfit'] font-bold text-slate-900">Contact Information</h2>
                  <p className="text-sm md:text-base text-slate-500 mt-1">Let's start with your basic info</p>
                </div>

                <div className="grid grid-cols-2 gap-3 md:gap-4">
                  <div>
                    <Label htmlFor="first_name" className="text-sm">First Name *</Label>
                    <Input
                      id="first_name"
                      value={formData.first_name}
                      onChange={(e) => updateForm("first_name", e.target.value)}
                      className="mt-1.5 h-11 md:h-12"
                      data-testid="first-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name" className="text-sm">Last Name *</Label>
                    <Input
                      id="last_name"
                      value={formData.last_name}
                      onChange={(e) => updateForm("last_name", e.target.value)}
                      className="mt-1.5 h-11 md:h-12"
                      data-testid="last-name-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email" className="text-sm">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => updateForm("email", e.target.value)}
                    className="mt-1.5 h-11 md:h-12"
                    data-testid="email-input"
                  />
                </div>

                <div>
                  <Label htmlFor="phone" className="text-sm">Phone Number *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => updateForm("phone", e.target.value)}
                    className="mt-1.5 h-11 md:h-12"
                    data-testid="phone-input"
                  />
                </div>

                <Button
                  onClick={() => setStep(2)}
                  disabled={!isStep1Valid}
                  className="w-full h-12 md:h-14 text-base md:text-lg bg-[#A94442] hover:bg-[#8B3836] rounded-full"
                  data-testid="step1-next-btn"
                >
                  Continue <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            )}

            {/* Step 2: Address */}
            {step === 2 && (
              <div className="space-y-4 md:space-y-6 animate-fade-in">
                <div>
                  <h2 className="text-xl md:text-2xl font-['Outfit'] font-bold text-slate-900">Property Location</h2>
                  <p className="text-sm md:text-base text-slate-500 mt-1">Where would you like solar installed?</p>
                </div>

                {/* Service Area Selection */}
                <div>
                  <Label htmlFor="service_area" className="text-sm">Service Area *</Label>
                  <Select value={formData.service_area} onValueChange={handleServiceAreaChange}>
                    <SelectTrigger className="mt-1.5 h-11 md:h-12" data-testid="service-area-select">
                      <SelectValue placeholder="Select your area" />
                    </SelectTrigger>
                    <SelectContent>
                      <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50">Richmond Office Territory</div>
                      {richmondAreas.map(area => (
                        <SelectItem key={area.value} value={area.value}>{area.label}</SelectItem>
                      ))}
                      <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50 mt-1">Williamsburg Office Territory</div>
                      {williamsburgAreas.map(area => (
                        <SelectItem key={area.value} value={area.value}>{area.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="address" className="text-sm">Street Address *</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => updateForm("address", e.target.value)}
                    className="mt-1.5 h-11 md:h-12"
                    data-testid="address-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3 md:gap-4">
                  <div>
                    <Label htmlFor="city" className="text-sm">City *</Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => updateForm("city", e.target.value)}
                      className="mt-1.5 h-11 md:h-12"
                      data-testid="city-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="zip" className="text-sm">ZIP Code *</Label>
                    <Input
                      id="zip"
                      value={formData.zip_code}
                      onChange={(e) => updateForm("zip_code", e.target.value)}
                      className="mt-1.5 h-11 md:h-12"
                      data-testid="zip-input"
                    />
                  </div>
                </div>

                {/* Power Company Selection */}
                <div>
                  <Label htmlFor="power_company" className="text-sm">Power Company *</Label>
                  <Select value={formData.power_company} onValueChange={handlePowerCompanyChange}>
                    <SelectTrigger className="mt-1.5 h-11 md:h-12" data-testid="power-company-select">
                      <SelectValue placeholder="Select your power provider" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="dominion">Dominion Energy</SelectItem>
                      <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50 mt-1">Co-op Power Companies</div>
                      {coopPowerCompanies.map(company => (
                        <SelectItem key={company.value} value={company.value}>{company.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {/* Co-op confirmation message */}
                  {pendingConfirmation && (
                    <div className="mt-3 p-3 bg-[#FDB813]/10 rounded-lg border border-[#FDB813]/30">
                      <p className="text-sm text-amber-800 font-medium">Co-op power companies require confirmation</p>
                      <p className="text-xs text-amber-700 mt-1">
                        Please upload your bill and we'll confirm if we can service your area. You'll receive confirmation from Anthony F.
                      </p>
                    </div>
                  )}
                </div>

                <div>
                  <Label htmlFor="roof_type" className="text-sm">Roof Type *</Label>
                  <Select 
                    value={formData.roof_type} 
                    onValueChange={(v) => {
                      updateForm("roof_type", v);
                      // Check roof type qualification
                      if (v === "slate") {
                        // Slate is automatically disqualified
                        handleDisqualification("Slate roofs are not compatible with our solar panel installation. Unfortunately, we cannot proceed with your application.");
                      } else if (v === "3_tab") {
                        setRoofQuoteNeeded(true);
                        setRoofDisqualified(false);
                      } else if (["tile", "wood_shake", "other"].includes(v)) {
                        setRoofDisqualified(true);
                        setRoofQuoteNeeded(false);
                      } else {
                        setRoofQuoteNeeded(false);
                        setRoofDisqualified(false);
                      }
                    }}
                  >
                    <SelectTrigger className="mt-1.5 h-11 md:h-12" data-testid="roof-type-select">
                      <SelectValue placeholder="Select roof type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="architectural_shingle">Architectural Shingles</SelectItem>
                      <SelectItem value="3_tab">3-Tab Shingles</SelectItem>
                      <SelectItem value="metal">Metal (with beams)</SelectItem>
                      <SelectItem value="flat">Flat Roof</SelectItem>
                      <SelectItem value="tile">Tile</SelectItem>
                      <SelectItem value="slate">Slate</SelectItem>
                      <SelectItem value="wood_shake">Wood Shake</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  {/* Roof Quote Message */}
                  {roofQuoteNeeded && (
                    <div className="mt-3 p-3 bg-[#FDB813]/10 rounded-lg border border-[#FDB813]/30">
                      <p className="text-sm text-amber-800 font-medium">3-Tab shingles require a roof quote</p>
                      <p className="text-xs text-amber-700 mt-1">
                        We offer roof replacement services. You can continue and we'll discuss options during your consultation.
                      </p>
                    </div>
                  )}
                  
                  {/* Roof Disqualification Message */}
                  {roofDisqualified && (
                    <div className="mt-3 p-3 bg-[#A94442]/10 rounded-lg border border-[#A94442]/30">
                      <p className="text-sm text-[#A94442] font-medium">This roof type may not qualify</p>
                      <p className="text-xs text-slate-600 mt-1">
                        Email a clear picture of your roof to confirm eligibility:
                      </p>
                      <a 
                        href="mailto:Anthony.fryer@ionsolar.com?subject=Roof%20Type%20Confirmation&body=Hi%20Anthony,%0A%0APlease%20see%20attached%20picture%20of%20my%20roof%20for%20solar%20installation%20eligibility.%0A%0AThank%20you!"
                        className="text-sm font-medium text-[#A94442] underline mt-2 inline-block"
                      >
                        Anthony.fryer@ionsolar.com
                      </a>
                    </div>
                  )}
                </div>

                {/* Tree Shading Question */}
                <div>
                  <Label className="mb-3 block text-sm">Is the south-facing side of your roof shaded by trees? *</Label>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={() => updateForm("has_tree_shading", true)}
                      className={`flex-1 py-3 px-4 rounded-xl border-2 font-medium text-sm transition-all ${
                        formData.has_tree_shading === true
                          ? "border-[#A94442] bg-[#A94442]/5 text-[#A94442]"
                          : "border-slate-200 text-slate-600 hover:border-slate-300"
                      }`}
                      data-testid="tree-shading-yes-btn"
                    >
                      Yes, it's shaded
                    </button>
                    <button
                      onClick={() => {
                        updateForm("has_tree_shading", false);
                        updateForm("open_to_tree_removal", null);
                      }}
                      className={`flex-1 py-3 px-4 rounded-xl border-2 font-medium text-sm transition-all ${
                        formData.has_tree_shading === false
                          ? "border-[#1A4D2E] bg-[#1A4D2E]/5 text-[#1A4D2E]"
                          : "border-slate-200 text-slate-600 hover:border-slate-300"
                      }`}
                      data-testid="tree-shading-no-btn"
                    >
                      No, it gets good sun
                    </button>
                  </div>
                  
                  {/* Follow-up: Open to tree removal */}
                  {formData.has_tree_shading === true && (
                    <div className="mt-4 p-4 bg-slate-50 rounded-xl">
                      <Label className="mb-3 block text-sm">Would you be open to tree removal to improve sun exposure?</Label>
                      <div className="flex flex-col sm:flex-row gap-3">
                        <button
                          onClick={() => updateForm("open_to_tree_removal", true)}
                          className={`flex-1 py-3 px-4 rounded-xl border-2 font-medium text-sm transition-all ${
                            formData.open_to_tree_removal === true
                              ? "border-[#1A4D2E] bg-[#1A4D2E]/5 text-[#1A4D2E]"
                              : "border-slate-200 text-slate-600 hover:border-slate-300"
                          }`}
                          data-testid="tree-removal-yes-btn"
                        >
                          Yes, I'm open to it
                        </button>
                        <button
                          onClick={() => handleDisqualification("Unfortunately, solar panels require adequate sunlight. Since your south-facing roof is shaded and tree removal isn't an option, we cannot proceed with your application at this time.")}
                          className={`flex-1 py-3 px-4 rounded-xl border-2 font-medium text-sm transition-all ${
                            formData.open_to_tree_removal === false
                              ? "border-[#A94442] bg-[#A94442]/5 text-[#A94442]"
                              : "border-slate-200 text-slate-600 hover:border-slate-300"
                          }`}
                          data-testid="tree-removal-no-btn"
                        >
                          No, I'd prefer not to
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <Label htmlFor="bill" className="text-sm">Upload Electricity Bill *</Label>
                  <p className="text-xs text-slate-500 mb-2">Upload a copy of your full bill so we can review your energy usage</p>
                  <div className="relative">
                    <input
                      id="bill"
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setBillFile(file);
                          updateForm("bill_filename", file.name);
                        }
                      }}
                      className="hidden"
                      data-testid="bill-upload-input"
                    />
                    <label
                      htmlFor="bill"
                      className={`flex items-center justify-center gap-2 w-full h-24 border-2 border-dashed rounded-xl cursor-pointer transition-all ${
                        billFile 
                          ? "border-[#1A4D2E] bg-[#1A4D2E]/5" 
                          : "border-slate-300 hover:border-slate-400 bg-slate-50"
                      }`}
                    >
                      {billFile ? (
                        <div className="text-center">
                          <CheckCircle2 className="w-6 h-6 text-[#1A4D2E] mx-auto mb-1" />
                          <p className="text-sm font-medium text-[#1A4D2E]">{billFile.name}</p>
                          <p className="text-xs text-slate-500">Click to change</p>
                        </div>
                      ) : (
                        <div className="text-center">
                          <Upload className="w-6 h-6 text-slate-400 mx-auto mb-1" />
                          <p className="text-sm text-slate-600">Click to upload your bill</p>
                          <p className="text-xs text-slate-400">PDF, PNG, or JPG</p>
                        </div>
                      )}
                    </label>
                  </div>
                </div>

                <div className="flex gap-3 md:gap-4">
                  <Button
                    variant="outline"
                    onClick={() => setStep(1)}
                    className="flex-1 h-12 md:h-14 rounded-full"
                    data-testid="step2-back-btn"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={() => setStep(3)}
                    disabled={!isStep2Valid}
                    className="flex-1 h-12 md:h-14 bg-[#A94442] hover:bg-[#8B3836] rounded-full"
                    data-testid="step2-next-btn"
                  >
                    Continue <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Qualification */}
            {step === 3 && (
              <div className="space-y-4 md:space-y-6 animate-fade-in">
                <div>
                  <h2 className="text-xl md:text-2xl font-['Outfit'] font-bold text-slate-900">Quick Qualification</h2>
                  <p className="text-sm md:text-base text-slate-500 mt-1">Help us understand your situation</p>
                </div>

                <div>
                  <Label className="mb-3 block text-sm">Are you the homeowner? *</Label>
                  <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
                    <button
                      onClick={() => updateForm("is_homeowner", true)}
                      className={`flex-1 py-3 md:py-4 px-4 md:px-6 rounded-xl border-2 font-medium text-sm md:text-base transition-all ${
                        formData.is_homeowner === true
                          ? "border-[#A94442] bg-[#A94442]/5 text-[#A94442]"
                          : "border-slate-200 text-slate-600 hover:border-slate-300"
                      }`}
                      data-testid="homeowner-yes-btn"
                    >
                      Yes, I own my home
                    </button>
                    <button
                      onClick={() => handleDisqualification("Renters do not qualify for our PPA program. Only homeowners are eligible for solar installation.")}
                      className={`flex-1 py-3 md:py-4 px-4 md:px-6 rounded-xl border-2 font-medium text-sm md:text-base transition-all ${
                        formData.is_homeowner === false
                          ? "border-[#A94442] bg-[#A94442]/5 text-[#A94442]"
                          : "border-slate-200 text-slate-600 hover:border-slate-300"
                      }`}
                      data-testid="homeowner-no-btn"
                    >
                      No, I rent
                    </button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="credit" className="text-sm">Credit Score Range *</Label>
                  <Select 
                    value={formData.credit_score_range} 
                    onValueChange={(v) => {
                      if (v === "below_630") {
                        handleDisqualification("A credit score of 630 or higher is required to qualify for our PPA program.");
                      } else {
                        updateForm("credit_score_range", v);
                      }
                    }}
                  >
                    <SelectTrigger className="mt-1.5 h-11 md:h-12" data-testid="credit-score-select">
                      <SelectValue placeholder="Select range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="750+">Excellent (750+)</SelectItem>
                      <SelectItem value="700-749">Good (700-749)</SelectItem>
                      <SelectItem value="630-699">Fair (630-699)</SelectItem>
                      <SelectItem value="below_630">Below 630</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-2">
                    * Credit score of 630+ required for PPA qualification
                  </p>
                </div>

                <div className="flex items-start gap-3 p-3 md:p-4 bg-slate-50 rounded-xl">
                  <Checkbox
                    id="ontime"
                    checked={formData.makes_on_time_payments === true}
                    onCheckedChange={(checked) => updateForm("makes_on_time_payments", checked)}
                    data-testid="ontime-payments-checkbox"
                  />
                  <div>
                    <Label htmlFor="ontime" className="cursor-pointer text-sm">I make on-time payments</Label>
                    <p className="text-xs text-slate-500 mt-1">
                      Consistent payment history helps with qualification
                    </p>
                  </div>
                </div>

                <div className="flex gap-3 md:gap-4">
                  <Button
                    variant="outline"
                    onClick={() => setStep(2)}
                    className="flex-1 h-12 md:h-14 rounded-full"
                    data-testid="step3-back-btn"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={!isStep3Valid || loading}
                    className="flex-1 h-12 md:h-14 bg-[#A94442] hover:bg-[#8B3836] rounded-full"
                    data-testid="step3-next-btn"
                  >
                    {loading ? (
                      <div className="spinner" />
                    ) : (
                      <>Continue <ArrowRight className="w-5 h-5 ml-2" /></>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Step 4: Schedule a Quick Call */}
            {step === 4 && (
              <div className="space-y-4 md:space-y-6 animate-fade-in">
                <div>
                  <h2 className="text-xl md:text-2xl font-['Outfit'] font-bold text-slate-900">Schedule a Quick Call</h2>
                  <p className="text-sm md:text-base text-slate-500 mt-1">Anthony will call you to gather info and walk you through what to expect at your consultation</p>
                </div>

                {/* Info Notice Banner */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3" data-testid="call-info-notice">
                  <Phone className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-blue-800">
                    <span className="font-medium">Please note:</span> Anthony may be in a home with a family at the time of your call, but he will reach out to you as soon as possible!
                  </p>
                </div>

                {slotsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="spinner" />
                    <span className="ml-3 text-slate-500">Loading available times...</span>
                  </div>
                ) : (
                  <>
                    {/* Day Selector */}
                    <div>
                      <Label className="text-sm mb-2 block">Select a Day *</Label>
                      <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
                        {availableSlots.map((day) => (
                          <button
                            key={day.date}
                            onClick={() => {
                              setSelectedBookingDay(day);
                              setSelectedBookingSlot(null);
                            }}
                            className={`flex-shrink-0 px-4 py-3 rounded-xl border-2 text-center transition-all min-w-[100px] ${
                              selectedBookingDay?.date === day.date
                                ? "border-blue-600 bg-blue-50 text-blue-600"
                                : "border-slate-200 hover:border-slate-300 text-slate-600"
                            }`}
                            data-testid={`call-day-btn-${day.date}`}
                          >
                            <div className="text-xs font-medium">{day.day_name}</div>
                            <div className="text-sm font-bold mt-1">
                              {new Date(day.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Time Slots */}
                    {selectedBookingDay && (
                      <div>
                        <Label className="text-sm mb-2 block">
                          Select a Time * (Eastern Time)
                        </Label>
                        {selectedBookingDay.slots.filter(s => s.is_available).length === 0 ? (
                          <div className="bg-slate-50 rounded-xl p-4 text-center">
                            <p className="text-slate-500 text-sm">No times available for this day</p>
                            <p className="text-xs text-slate-400 mt-1">Please select another day</p>
                          </div>
                        ) : (
                          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                            {selectedBookingDay.slots.map((slot, idx) => (
                              <button
                                key={idx}
                                onClick={() => slot.is_available && setSelectedBookingSlot(slot)}
                                disabled={!slot.is_available}
                                className={`py-3 px-2 rounded-xl text-sm font-medium transition-all ${
                                  !slot.is_available
                                    ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                                    : selectedBookingSlot?.datetime_utc === slot.datetime_utc
                                      ? "bg-blue-600 text-white"
                                      : slot.is_request_only
                                        ? "border-2 border-dashed border-[#FDB813] text-[#FDB813] hover:bg-[#FDB813]/10"
                                        : "border-2 border-slate-200 text-slate-700 hover:border-slate-400 hover:bg-slate-50"
                                }`}
                                data-testid={`call-slot-btn-${slot.time.replace(/[:\s]/g, '-')}`}
                              >
                                {slot.time}
                                {slot.is_request_only && (
                                  <div className="text-[10px] mt-0.5 opacity-75">Request</div>
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                        {selectedBookingDay.slots.some(s => s.is_request_only) && (
                          <p className="text-xs text-slate-500 mt-2">
                            <span className="inline-block w-3 h-3 border border-dashed border-[#FDB813] rounded mr-1"></span>
                            Dashed slots are special request times
                          </p>
                        )}
                      </div>
                    )}

                    {/* Selected Time Summary */}
                    {selectedBookingSlot && (
                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                        <div className="flex items-start gap-3">
                          <PhoneCall className="w-5 h-5 text-blue-600 mt-0.5" />
                          <div>
                            <p className="font-medium text-sm text-blue-600">Selected Call Time</p>
                            <p className="text-base font-semibold text-slate-900 mt-1">
                              {selectedBookingDay.day_name}, {new Date(selectedBookingDay.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} at {selectedBookingSlot.time} ET
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}

                <div className="flex gap-3 md:gap-4">
                  <Button
                    variant="outline"
                    onClick={() => setStep(3)}
                    className="flex-1 h-12 md:h-14 rounded-full"
                    data-testid="step4-back-btn"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleCallConfirm}
                    disabled={!selectedBookingSlot || bookingLoading}
                    className="flex-1 h-12 md:h-14 bg-blue-600 hover:bg-blue-700 rounded-full text-base md:text-lg"
                    data-testid="schedule-call-btn"
                  >
                    {bookingLoading ? (
                      <div className="spinner" />
                    ) : (
                      <>Schedule Call</>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Step 5: Request Your In-Home Consultation */}
            {step === 5 && (
              <div className="space-y-4 md:space-y-6 animate-fade-in">
                <div>
                  <h2 className="text-xl md:text-2xl font-['Outfit'] font-bold text-slate-900">Request Your In-Home Consultation</h2>
                  <p className="text-sm md:text-base text-slate-500 mt-1">Pick your preferred time. Anthony will confirm shortly.</p>
                </div>

                {/* Show call booking confirmation */}
                {callBooking && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-blue-600" />
                      <span className="text-sm text-blue-600 font-medium">Call scheduled: {callBooking.slot_datetime_eastern}</span>
                    </div>
                  </div>
                )}

                {slotsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="spinner" />
                    <span className="ml-3 text-slate-500">Loading available times...</span>
                  </div>
                ) : (
                  <>
                    {/* Day Selector */}
                    <div>
                      <Label className="text-sm mb-2 block">Select a Day *</Label>
                      <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
                        {appointmentSlots.map((day) => (
                          <button
                            key={day.date}
                            onClick={() => {
                              setSelectedAppointmentDay(day);
                              setSelectedAppointmentSlot(null);
                            }}
                            className={`flex-shrink-0 px-4 py-3 rounded-xl border-2 text-center transition-all min-w-[100px] ${
                              selectedAppointmentDay?.date === day.date
                                ? "border-[#A94442] bg-[#A94442]/5 text-[#A94442]"
                                : "border-slate-200 hover:border-slate-300 text-slate-600"
                            }`}
                            data-testid={`appt-day-btn-${day.date}`}
                          >
                            <div className="text-xs font-medium">{day.day_name}</div>
                            <div className="text-sm font-bold mt-1">
                              {new Date(day.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Time Slots */}
                    {selectedAppointmentDay && (
                      <div>
                        <Label className="text-sm mb-2 block">
                          Select a Time * (Eastern Time)
                        </Label>
                        {selectedAppointmentDay.slots.filter(s => s.is_available).length === 0 ? (
                          <div className="bg-slate-50 rounded-xl p-4 text-center">
                            <p className="text-slate-500 text-sm">No times available for this day</p>
                            <p className="text-xs text-slate-400 mt-1">Please select another day</p>
                          </div>
                        ) : (
                          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                            {selectedAppointmentDay.slots.map((slot, idx) => (
                              <button
                                key={idx}
                                onClick={() => slot.is_available && setSelectedAppointmentSlot(slot)}
                                disabled={!slot.is_available}
                                className={`py-3 px-2 rounded-xl text-sm font-medium transition-all ${
                                  !slot.is_available
                                    ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                                    : selectedAppointmentSlot?.datetime_utc === slot.datetime_utc
                                      ? "bg-[#A94442] text-white"
                                      : slot.is_request_only
                                        ? "border-2 border-dashed border-[#FDB813] text-[#FDB813] hover:bg-[#FDB813]/10"
                                        : "border-2 border-slate-200 text-slate-700 hover:border-slate-400 hover:bg-slate-50"
                                }`}
                                data-testid={`appt-slot-btn-${slot.time.replace(/[:\s]/g, '-')}`}
                              >
                                {slot.time}
                                {slot.is_request_only && (
                                  <div className="text-[10px] mt-0.5 opacity-75">Request</div>
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                        {selectedAppointmentDay.slots.some(s => s.is_request_only) && (
                          <p className="text-xs text-slate-500 mt-2">
                            <span className="inline-block w-3 h-3 border border-dashed border-[#FDB813] rounded mr-1"></span>
                            Dashed slots are special request times
                          </p>
                        )}
                      </div>
                    )}

                    {/* Selected Time Summary */}
                    {selectedAppointmentSlot && (
                      <div className="bg-[#1A4D2E]/5 rounded-xl p-4">
                        <div className="flex items-start gap-3">
                          <Home className="w-5 h-5 text-[#1A4D2E] mt-0.5" />
                          <div>
                            <p className="font-medium text-sm text-[#1A4D2E]">Selected Consultation Time</p>
                            <p className="text-base font-semibold text-slate-900 mt-1">
                              {selectedAppointmentDay.day_name}, {new Date(selectedAppointmentDay.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} at {selectedAppointmentSlot.time} ET
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Pending Confirmation Notice */}
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <Clock className="w-5 h-5 text-amber-600 mt-0.5" />
                        <div>
                          <p className="font-medium text-sm text-amber-700">⏳ Pending Confirmation</p>
                          <p className="text-xs text-amber-600 mt-1">
                            Anthony will confirm your appointment via email and text shortly.
                          </p>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                <div className="flex gap-3 md:gap-4">
                  <Button
                    variant="outline"
                    onClick={() => setStep(4)}
                    disabled={true}
                    className="flex-1 h-12 md:h-14 rounded-full opacity-50 cursor-not-allowed"
                    data-testid="step5-back-btn"
                    title="Call already scheduled - cannot go back"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleAppointmentConfirm}
                    disabled={!selectedAppointmentSlot || bookingLoading}
                    className="flex-1 h-12 md:h-14 bg-[#A94442] hover:bg-[#8B3836] rounded-full text-base md:text-lg"
                    data-testid="request-appointment-btn"
                  >
                    {bookingLoading ? (
                      <div className="spinner" />
                    ) : (
                      <>Request Appointment</>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Testimonials */}
          <div className="space-y-4 md:space-y-6">
            <h2 className="text-xl md:text-2xl font-['Outfit'] font-bold text-slate-900">
              What Our Customers Say
            </h2>
            
            {testimonials.map((testimonial, index) => (
              <div 
                key={index}
                className="testimonial-card stagger-item"
              >
                <div className="flex gap-1 mb-2 md:mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-3 h-3 md:w-4 md:h-4 fill-[#FDB813] text-[#FDB813]" />
                  ))}
                </div>
                <p className="text-sm md:text-base text-slate-700 italic mb-3 md:mb-4">"{testimonial.content}"</p>
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-[#1A4D2E]/10 flex items-center justify-center">
                    <span className="text-xs font-semibold text-[#1A4D2E]">
                      {testimonial.author_name ? testimonial.author_name[0] : "?"}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-sm text-slate-900">{testimonial.author_name}</p>
                    <p className="text-xs text-slate-500">{testimonial.location}</p>
                  </div>
                </div>
              </div>
            ))}

            {/* Leave a Review */}
            <TestimonialForm />

            {/* Trust Badges */}
            <div className="bg-[#1A4D2E]/5 rounded-xl p-4 md:p-6">
              <h3 className="font-['Outfit'] font-semibold text-[#1A4D2E] mb-3 md:mb-4">Why Choose Ion Solar?</h3>
              <ul className="space-y-2 md:space-y-3">
                <li className="flex items-center gap-2 md:gap-3 text-xs md:text-sm text-slate-700">
                  <CheckCircle2 className="w-4 h-4 md:w-5 md:h-5 text-[#1A4D2E] flex-shrink-0" />
                  85,000+ installations nationwide
                </li>
                <li className="flex items-center gap-2 md:gap-3 text-xs md:text-sm text-slate-700">
                  <CheckCircle2 className="w-4 h-4 md:w-5 md:h-5 text-[#1A4D2E] flex-shrink-0" />
                  Zero upfront cost with PPA
                </li>
                <li className="flex items-center gap-2 md:gap-3 text-xs md:text-sm text-slate-700">
                  <CheckCircle2 className="w-4 h-4 md:w-5 md:h-5 text-[#1A4D2E] flex-shrink-0" />
                  Professional installation in 3-4 weeks
                </li>
                <li className="flex items-center gap-2 md:gap-3 text-xs md:text-sm text-slate-700">
                  <CheckCircle2 className="w-4 h-4 md:w-5 md:h-5 text-[#1A4D2E] flex-shrink-0" />
                  Virginia-based support team
                </li>
              </ul>
            </div>

            {/* Recent Leads - Social Proof */}
            {recentLeads.length > 0 && (
              <div className="bg-white rounded-xl p-4 md:p-6 border border-slate-200">
                <div className="flex items-center gap-2 mb-4">
                  <Users className="w-5 h-5 text-[#1A4D2E]" />
                  <h3 className="font-['Outfit'] font-semibold text-slate-900">Recent Inquiries</h3>
                </div>
                <div className="space-y-3">
                  {recentLeads.slice(0, 5).map((lead, index) => (
                    <div 
                      key={index}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#1A4D2E]/10 flex items-center justify-center">
                          <span className="text-xs font-semibold text-[#1A4D2E]">
                            {lead.first_name ? lead.first_name[0].toUpperCase() : "?"}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-900">
                            {lead.first_name} {lead.last_initial}
                          </p>
                          <div className="flex items-center gap-1 text-xs text-slate-500">
                            <MapPin className="w-3 h-3" />
                            {lead.city}, {lead.state} {lead.zip_code}
                          </div>
                        </div>
                      </div>
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                        lead.is_qualified 
                          ? "bg-[#1A4D2E]/10 text-[#1A4D2E]" 
                          : "bg-slate-200 text-slate-600"
                      }`}>
                        {lead.is_qualified ? "Qualified" : "Reviewed"}
                      </span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-3 text-center">
                  Join {recentLeads.length}+ Virginia homeowners exploring solar
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
